'use client';
import dynamic from 'next/dynamic';
import { useCallback, useMemo, useRef, useState } from 'react';

import type { Source } from '~context/media-content/types';

import { MediaContentContext } from './index';
const MediaContentModal = dynamic(
	() =>
		import('~components/Modals/MediaContentModal').then(
			(module) => module.MediaContentModal
		),
	{
		ssr: false,
		loading: () => null,
	}
);
type Props = {
	children: React.ReactNode;
};

export const MediaContentContextProvider = ({ children }: Props) => {
	const [openedId, open] = useState<number | null>(null);
	const [media, setMedia] = useState<{
		source: Source[];
		currentIndex: number;
	}>({
		source: [],
		currentIndex: 0,
	});

	const onClose = useCallback(() => {
		open(null);
	}, []);

	const actions = useRef<{ [key: string]: () => void | undefined }>({});
	const onOpen = useCallback((src: Source[], index: number) => {
		src.forEach((s, index) => {
			if (s.payload?.action) {
				actions.current[index] = s.payload.action;
			}
		});

		setMedia({
			source: src,
			currentIndex: index,
		});
		open(src[0].messageId);
	}, []);

	const value = useMemo(
		() => ({
			open: onOpen,
			close: onClose,
			openedId,
			update: (src: Source[]) => {
				src.forEach((s, index) => {
					if (s.payload?.action) {
						actions.current[index] = s.payload.action;
					}
				});

				setMedia({
					...media,
					source: src,
				});
			},
		}),
		[media, onClose, onOpen, openedId]
	);

	const handleAction = useCallback((index: number) => {
		actions.current[index]?.();
	}, []);

	return (
		<MediaContentContext.Provider value={value}>
			{openedId && media.source.length && (
				<MediaContentModal
					source={media.source}
					startFromIndex={media.currentIndex}
					onClose={onClose}
					onAction={handleAction}
					onNavAction={media.source[0].commonNavAction}
				/>
			)}
			{children}
		</MediaContentContext.Provider>
	);
};
